/* eslint-disable @nx/enforce-module-boundaries */
import { inject, Injectable } from "@angular/core";
import { rxResource, toSignal } from "@angular/core/rxjs-interop";
import { Preferences } from "@capacitor/preferences";
import {
    CenterDto,
    CenterService,
    ChildDto,
    ChildService,
    SearchChildResponseDto,
} from "@malirang/api-client";
import { SelectedChild } from "@malirang/common";
import { BaseStore } from "@malirang/front-share";
import { UserCenterControllerFindByChildId$Params } from "libs/api-client/src/lib/fn/center/user-center-controller-find-by-child-id";
import { ParentChildControllerSearchByParent$Params } from "libs/api-client/src/lib/fn/child/parent-child-controller-search-by-parent";
import { toObservableSignal } from "ngxtension/to-observable-signal";
import { lastValueFrom, map, tap } from "rxjs";

type State = {
    children: ChildDto[];
    selectedChild: ChildDto | null;
    selectedChildCenters: CenterDto[];
};

/**
 * @name ChildStore
 * @description 내 아동 관련 상태를 관리합니다.
 * @returns {void}
 */
@Injectable({ providedIn: "root" })
export class ChildStore extends BaseStore<State> {
    private readonly childService = inject(ChildService);
    private readonly centerService = inject(CenterService);

    /**
     * @name children
     * @description 내 아동 목록을 반환합니다.
     * @returns {Signal<ChildDto[]>} 내 아동 목록
     */
    readonly children = toSignal<ChildDto[]>(
        this.state$.pipe(map((state) => state.children)),
    );

    /**
     * @name selectedChild
     * @description 선택된 아동을 반환합니다.
     * @returns {Signal<ChildDto | null>} 선택된 아동
     */
    readonly selectedChild$ = toObservableSignal<ChildDto | null>(
        this.state$.pipe(map((state) => state.selectedChild)),
    );

    /**
     * @name selectedChildCenters
     * @description 선택된 아동의 센터 목록을 반환합니다.
     * @returns {Signal<CenterDto[]>} 선택된 아동의 센터 목록
     */
    readonly selectedChildCenters$ = toObservableSignal<CenterDto[]>(
        this.state$.pipe(map((state) => state.selectedChildCenters)),
    );

    /**
     * @name search$
     * @description 내 아동 목록을 조회합니다.
     */
    private readonly search$ = rxResource({
        params: () =>
            ({
                pageNo: 1,
                pageSize: 9999,
            }) as ParentChildControllerSearchByParent$Params,
        stream: ({ params }) =>
            this.childService
                .parentChildControllerSearchByParent({
                    ...params,
                })
                .pipe(
                    tap(async (res: SearchChildResponseDto) => {
                        if (res?.items?.length > 0) {
                            try {
                                const selectedChildId = await Preferences?.get({
                                    key: SelectedChild,
                                });

                                const selectedChild = res.items.find(
                                    (child) =>
                                        child?.id === selectedChildId?.value,
                                );
                                if (selectedChildId?.value && selectedChild) {
                                    await this.setSelectedChild(selectedChild);
                                } else {
                                    await this.setSelectedChild(res.items[0]);
                                }

                                this.setChildren(res.items);
                                this.centers$.reload();
                            } catch (error) {
                                console.error("Preferences get error:", error);
                            }
                        } else {
                            await this.clear();
                        }
                    }),
                ),
    });

    public async childFetchAll(): Promise<SearchChildResponseDto> {
        const children = await lastValueFrom(
            this.childService.parentChildControllerSearchByParent({
                pageNo: 1,
                pageSize: 9999,
            }),
        );

        this.setChildren(children.items || []);
        if (children?.items?.length) {
            try {
                const selectedChildId = await Preferences?.get({
                    key: SelectedChild,
                });

                const selectedChild = children.items.find(
                    (child) => child?.id === selectedChildId?.value,
                );
                if (selectedChildId?.value && selectedChild) {
                    await this.setSelectedChild(selectedChild);
                } else {
                    await this.setSelectedChild(children.items[0]);
                }
            } catch (error) {
                console.error("Preferences get error:", error);
            }
        }

        return children as SearchChildResponseDto;
    }

    /**
     * @name centers$
     * @description 선택된 아동의 센터 목록을 조회합니다.
     */
    private readonly centers$ = rxResource({
        params: () =>
            ({
                childId: this.selectedChild$()?.id || "",
            }) as UserCenterControllerFindByChildId$Params,
        stream: ({ params }) =>
            this.centerService.userCenterControllerFindByChildId(params).pipe(
                tap((centers: CenterDto[]) => {
                    if (centers && centers.length)
                        this.updateState({
                            selectedChildCenters: centers || [],
                        });
                }),
            ),
    });

    constructor() {
        super({
            children: [],
            selectedChild: null,
            selectedChildCenters: [],
        });
    }

    /**
     * @name fetchAll
     * @description 전체 데이터를 조회합니다.
     * @returns {void}
     */
    fetchAll(): void {
        this.fetch();
        this.fetchCenters();
    }

    /**
     * @name fetch
     * @description 내 아동 목록을 조회합니다.
     * @returns {void}
     */
    fetch(): void {
        this.search$.reload();
    }

    /**
     * @name fetchCenters
     * @description 선택된 아동의 센터 목록을 조회합니다.
     * @returns {void}
     */
    fetchCenters(): void {
        this.centers$.reload();
    }

    /**
     * @name setChildren
     * @description 내 아동 목록을 설정합니다.
     * @param {ChildDto[]} children
     * @returns {void}
     */
    setChildren(children: ChildDto[]): void {
        this.updateState({ children });
    }

    /**
     * @name setSelectedChild
     * @description 선택된 아동을 설정합니다.
     * @param {ChildDto | null} selectedChild
     * @returns {Promise<void>}
     */
    async setSelectedChild(selectedChild: ChildDto | null): Promise<void> {
        this.updateState({ selectedChild });
        try {
            if (selectedChild) {
                await Preferences?.set({
                    key: SelectedChild,
                    value: selectedChild?.id || "",
                });
            } else {
                await Preferences?.remove({ key: SelectedChild });
            }
        } catch (error) {
            console.error("Preferences set error:", error);
        }
    }

    /**
     * @name setCenters
     * @description 선택된 아동의 센터 목록을 설정합니다.
     * @param {CenterDto[]} centers
     * @returns {void}
     */
    setCenters(centers: CenterDto[]): void {
        this.updateState({ selectedChildCenters: centers });
    }

    /**
     * @name clear
     * @description 상태를 초기화합니다.
     * @returns {Promise<void>}
     */
    async clear(): Promise<void> {
        this.setChildren([]);
        this.setCenters([]);
        await this.setSelectedChild(null);
    }
}
